import { SDI_STATI } from "./fatturazioneSdi";

/**
 * Importo/conteggio fatture attive Affidya nel periodo (mese YYYY-MM oppure anno).
 * Esclude note di credito, scarti e stornate.
 */
export function computeImportoFattureAttivePeriodo(fatture, { mese, anno } = {}) {
  const list = (fatture || []).filter((f) => {
    if (f?.tipo_documento === "nota_credito") return false;
    const stato = String(f?.stato || "").toLowerCase();
    if (["rifiutata", "scartata", "stornata"].includes(stato)) return false;
    const ref = f?.mese || String(f?.data_emissione || "").slice(0, 7);
    if (!ref) return false;
    if (mese) return ref === mese;
    if (anno != null) return ref.startsWith(String(anno));
    return true;
  });
  return {
    count: list.length,
    importo: list.reduce((a, f) => a + (Number(f.totale) || 0), 0),
  };
}

/** Differenza attive − passive (margine/utile di controllo). */
export function computeDifferenzaAttivePassive(attiveImporto, passiveImporto) {
  const a = Number(attiveImporto) || 0;
  const p = Number(passiveImporto) || 0;
  return {
    differenza: Math.round((a - p) * 100) / 100,
    attive: a,
    passive: p,
  };
}

/**
 * KPI unificati (6 card) per tutte le sottosezioni Fatturazione.
 * I valori variano per tab; la struttura resta identica.
 */
export function computeUnifiedKpis({
  activeTab,
  controlloKpi,
  fatturePerStruttura,
  meseControllo,
  filtroMese,
  sdiStats,
  archivioStats,
  attiveStats,
  loading,
}) {
  const meseRef =
    activeTab === "controllo-mensile"
      ? meseControllo
      : filtroMese && filtroMese !== "__tutti__"
        ? filtroMese
        : meseControllo;

  const fattureMese = (fatturePerStruttura || []).filter((f) => {
    const ref = f.mese || String(f.data_emissione || "").slice(0, 7);
    return ref === meseRef;
  });

  const attive = fattureMese.filter((f) => !["rifiutata", "scartata"].includes(f.stato));
  const bozze = attive.filter((f) => ["bozza", "generata", "approvata"].includes(f.stato));
  const daVerificare = attive.filter((f) => f.stato === "bozza");
  const inviate = attive.filter((f) => SDI_STATI.has(f.stato));
  const totalePrevisto = attive.reduce((a, f) => a + (f.totale || 0), 0);
  const totaleFatturato = inviate.reduce((a, f) => a + (f.totale || 0), 0);

  const fromControllo = controlloKpi || {};

  if (activeTab === "controllo-mensile" && Object.keys(fromControllo).length) {
    return {
      ospitiDaFatturare: fromControllo.ospiti_da_fatturare ?? 0,
      fattureGenerate: fromControllo.fatture_bozze ?? bozze.length,
      daVerificare: fromControllo.da_verificare ?? daVerificare.length,
      fattureInviate: fromControllo.inviate_sdi ?? inviate.length,
      totalePrevisto: fromControllo.totale_previsto ?? totalePrevisto,
      totaleFatturato: fromControllo.totale_fatturato ?? totaleFatturato,
      loading,
    };
  }

  if (activeTab === "fatture-sdi") {
    return {
      ospitiDaFatturare: fromControllo.ospiti_da_fatturare ?? attive.length,
      fattureGenerate: bozze.length,
      daVerificare: daVerificare.length,
      fattureInviate: sdiStats?.totale ?? inviate.length,
      totalePrevisto: fromControllo.totale_previsto ?? totalePrevisto,
      totaleFatturato: sdiStats?.importoTotale ?? totaleFatturato,
      loading,
    };
  }

  if (activeTab === "fatture-passive") {
    const passiveImporto =
      archivioStats?.importo_totale ??
      archivioStats?.importo_totale_mese ??
      archivioStats?.importo_totale_anno ??
      0;
    const attivePeriodo = computeImportoFattureAttivePeriodo(fatturePerStruttura, {
      mese: archivioStats?.mese || (filtroMese && filtroMese !== "__tutti__" ? filtroMese : null),
      anno: archivioStats?.mese ? null : archivioStats?.anno,
    });
    const { differenza } = computeDifferenzaAttivePassive(attivePeriodo.importo, passiveImporto);
    return {
      ospitiDaFatturare: attivePeriodo.count,
      fattureGenerate: archivioStats?.totale_fatture ?? 0,
      daVerificare: archivioStats?.da_pagare?.count ?? 0,
      fattureInviate: archivioStats?.scadute?.count ?? 0,
      totalePrevisto: attivePeriodo.importo,
      totaleFatturato: differenza,
      loading,
    };
  }

  if (activeTab === "fatture-attive") {
    return {
      ospitiDaFatturare: fromControllo.ospiti_da_fatturare ?? 0,
      fattureGenerate: attiveStats?.totale ?? 0,
      daVerificare: 0,
      fattureInviate: fromControllo.inviate_sdi ?? inviate.length,
      totalePrevisto: attiveStats?.importoTotale ?? totalePrevisto,
      totaleFatturato: attiveStats?.importoTotale ?? totaleFatturato,
      loading,
    };
  }

  if (activeTab === "note-credito") {
    const noteCredito = (fatturePerStruttura || []).filter((f) => f.tipo_documento === "nota_credito");
    const noteMese =
      filtroMese && filtroMese !== "__tutti__"
        ? noteCredito.filter((f) => (f.mese || String(f.data_emissione || "").slice(0, 7)) === filtroMese)
        : noteCredito;
    const ncInviate = noteMese.filter((f) => SDI_STATI.has(f.stato));
    const ncBozze = noteMese.filter((f) => ["bozza", "generata", "approvata"].includes(f.stato));
    const ncTotale = noteMese.reduce((a, f) => a + (f.totale || 0), 0);
    return {
      ospitiDaFatturare: noteMese.length,
      fattureGenerate: ncBozze.length,
      daVerificare: noteMese.filter((f) => f.stato === "bozza").length,
      fattureInviate: ncInviate.length,
      totalePrevisto: ncTotale,
      totaleFatturato: ncInviate.reduce((a, f) => a + (f.totale || 0), 0),
      loading,
    };
  }

  return {
    ospitiDaFatturare: fromControllo.ospiti_da_fatturare ?? attive.length,
    fattureGenerate: bozze.length,
    daVerificare: daVerificare.length,
    fattureInviate: inviate.length,
    totalePrevisto: fromControllo.totale_previsto ?? totalePrevisto,
    totaleFatturato: fromControllo.totale_fatturato ?? totaleFatturato,
    loading,
  };
}
